import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  Clock,
  Wifi,
  WifiOff,
  Database,
  Upload,
  Download,
  AlertCircle,
} from 'lucide-react';

interface SyncItem {
  id: string;
  type: 'photo' | 'checklist' | 'visit';
  description: string;
  timestamp: string;
}

export default function Sync() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(
    localStorage.getItem('lastSyncTime')
  );
  const [pendingItems, setPendingItems] = useState<SyncItem[]>([]);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState<string>('');

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load pending items from localStorage
    loadPendingItems();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadPendingItems = () => {
    try {
      const stored = localStorage.getItem('pendingSyncItems');
      if (stored) {
        setPendingItems(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading pending items:', error);
    }
  };

  const handleSync = async () => {
    if (!isOnline) {
      alert('Você está offline. Conecte-se à internet para sincronizar.');
      return;
    }

    setIsSyncing(true);
    setSyncProgress(0);
    setSyncStatus('Iniciando sincronização...');

    try {
      // Step 1: Sync pending uploads
      setSyncStatus('Enviando dados pendentes...');
      setSyncProgress(20);

      // Process pending items (this is a simulation - actual implementation would send to API)
      if (pendingItems.length > 0) {
        for (let i = 0; i < pendingItems.length; i++) {
          setSyncStatus(`Enviando ${i + 1} de ${pendingItems.length}...`);
          setSyncProgress(20 + (30 * (i + 1)) / pendingItems.length);
          // In production, send each item to the API here
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        // Clear pending items after successful sync
        localStorage.removeItem('pendingSyncItems');
        setPendingItems([]);
      }

      // Step 2: Refresh all data from server
      setSyncStatus('Atualizando dados...');
      setSyncProgress(60);

      await queryClient.invalidateQueries({ queryKey: ['route', 'today'] });
      setSyncProgress(70);

      await queryClient.invalidateQueries({ queryKey: ['visits', 'today'] });
      setSyncProgress(80);

      // Refetch all queries
      await queryClient.refetchQueries();
      setSyncProgress(90);

      // Step 3: Update last sync time
      const now = new Date().toISOString();
      localStorage.setItem('lastSyncTime', now);
      setLastSyncTime(now);
      setSyncProgress(100);
      setSyncStatus('Sincronização concluída!');

      setTimeout(() => {
        setIsSyncing(false);
        setSyncProgress(0);
        setSyncStatus('');
      }, 1500);
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus('Erro na sincronização');
      setTimeout(() => {
        setIsSyncing(false);
        setSyncProgress(0);
        setSyncStatus('');
      }, 2000);
    }
  };

  const formatRelativeTime = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'agora mesmo';
    if (diffMins < 60) return `há ${diffMins} min`;
    if (diffHours < 24) return `há ${diffHours}h`;
    return `há ${diffDays} dia(s)`;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'photo':
        return Upload;
      case 'checklist':
        return CheckCircle;
      case 'visit':
        return Database;
      default:
        return Database;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'photo':
        return 'Foto';
      case 'checklist':
        return 'Checklist';
      case 'visit':
        return 'Visita';
      default:
        return type;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Sincronização</h1>
          </div>
          <div className="flex items-center gap-2">
            {isOnline ? (
              <div className="flex items-center gap-1 px-3 py-1 bg-green-500/20 rounded-full">
                <Wifi className="h-4 w-4" />
                <span className="text-xs font-medium">Online</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-3 py-1 bg-red-500/20 rounded-full">
                <WifiOff className="h-4 w-4" />
                <span className="text-xs font-medium">Offline</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 space-y-4">
        {/* Sync Status Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center">
            {isSyncing ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <RefreshCw className="h-8 w-8 text-primary animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Sincronizando...
                </h3>
                <p className="text-sm text-gray-600 mb-4">{syncStatus}</p>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${syncProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">{syncProgress}%</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <RefreshCw className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Manter dados atualizados
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {lastSyncTime
                    ? `Última sincronização ${formatRelativeTime(lastSyncTime)}`
                    : 'Nunca sincronizado'}
                </p>
                <button
                  onClick={handleSync}
                  disabled={!isOnline}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="h-5 w-5" />
                  Sincronizar Agora
                </button>
                {!isOnline && (
                  <p className="text-xs text-red-600 mt-2">
                    Conecte-se à internet para sincronizar
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Pending Items */}
        {pendingItems.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-yellow-50">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <h2 className="font-semibold text-gray-900">
                  Itens Pendentes ({pendingItems.length})
                </h2>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Estes dados serão enviados na próxima sincronização
              </p>
            </div>

            <div className="divide-y divide-gray-100">
              {pendingItems.map((item) => {
                const Icon = getTypeIcon(item.type);
                return (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-yellow-100">
                        <Icon className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-yellow-700 uppercase">
                            {getTypeLabel(item.type)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatRelativeTime(item.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900">{item.description}</p>
                      </div>
                      <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sync Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Database className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-blue-900">
              <p className="font-medium mb-1">Modo Offline</p>
              <p className="text-blue-700">
                O app funciona offline. Seus dados são salvos localmente e
                sincronizados quando você se conectar à internet.
              </p>
            </div>
          </div>
        </div>

        {/* Sync Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-green-100">
                <Download className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-gray-900">
                  {queryClient.getQueryCache().getAll().length}
                </p>
                <p className="text-xs text-gray-600">Dados em cache</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-yellow-100">
                <Upload className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-gray-900">
                  {pendingItems.length}
                </p>
                <p className="text-xs text-gray-600">Aguardando envio</p>
              </div>
            </div>
          </div>
        </div>

        {/* Auto Sync Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Sincronização Automática</h3>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-sm text-gray-600">
            Seus dados são sincronizados automaticamente em segundo plano quando
            você está online.
          </p>
        </div>
      </main>
    </div>
  );
}
